import { useEffect, useRef } from 'react'
import { LineChart } from 'lucide-react'
import { Button } from '@/components'
import { useBacktestKpis } from '../hooks/useBacktestKpis'
import { useBacktestCoach } from '../backtestCoach'

/**
 * Results badge shown in chat under a completed backtest tool card. Numbers
 * only — all-neutral, no verdict / score / traffic light; the headline Return
 * carries the emphasis. KPIs are fetched by btid (they are not on the chat
 * message); the full skeleton renders immediately with — placeholders so the
 * card's height never jumps under the chat autoscroll.
 */

const QUESTION_COPY = 'Want me to run the optimizer to search for stronger settings?'

interface BacktestResultCardProps {
  btid: string
  msgId: string
  onRunOptimizer?: () => void
  onViewReport?: () => void
  /** Chat is busy — a new message can't be sent right now. */
  actionsDisabled?: boolean
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const fmtSignedPct = (v: number | undefined) => (isNum(v) ? `${v >= 0 ? '+' : ''}${v.toFixed(1)}%` : '—')
const fmtPct = (v: number | undefined) => (isNum(v) ? `${v.toFixed(1)}%` : '—')
const fmt2 = (v: number | undefined) => (isNum(v) ? v.toFixed(2) : '—')
const fmtInt = (v: number | undefined) => (isNum(v) ? String(Math.round(v)) : '—')

export default function BacktestResultCard({
  btid,
  msgId,
  onRunOptimizer,
  onViewReport,
  actionsDisabled = false,
}: BacktestResultCardProps) {
  const { data } = useBacktestKpis(btid)

  // First-run coach: while the tip points at this card, hold the actions
  // disabled — the user acknowledges the tip first, then the buttons unlock.
  const coach = useBacktestCoach()
  const locked = coach.step === 'card' && coach.msgId === msgId
  const disabled = locked || actionsDisabled

  // The badge appears inside an EXISTING chat message (the tool row flips to
  // done), so BotChat's append-based autoscroll never fires — the card can sit
  // below the fold, half-hidden behind the composer. While the coach spotlight
  // points at it, that's unusable: bring the whole card into view. (Optional
  // call: jsdom has no scrollIntoView.)
  const cardRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (locked) cardRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [locked])

  const secondary = [
    { label: 'Trades', value: fmtInt(data?.total_trades) },
    { label: 'Max drawdown', value: fmtPct(data?.max_drawdown) },
    { label: 'Win rate', value: fmtPct(data?.win_rate) },
    { label: 'Sharpe', value: fmt2(data?.sharpe_ratio) },
  ]

  return (
    <div
      ref={cardRef}
      data-coach="backtest-card"
      data-msg-id={msgId}
      className="shrink-0 rounded-lg border border-border bg-card shadow-card overflow-hidden"
    >
      <div className="px-4 pt-3 pb-3.5">
        {/* eyebrow — sober, monochrome; the celebratory weight comes from the headline number */}
        <div className="flex items-center gap-1.5 mb-2.5">
          <LineChart size={12} strokeWidth={1.75} className="text-muted-foreground shrink-0" />
          <span className="text-eyebrow text-muted-foreground">Backtest complete</span>
        </div>
        {/* headline Return — the number the user came for */}
        <div className="mb-3">
          <div className="text-caption text-muted-foreground">Return</div>
          <div className="text-heading text-foreground">{fmtSignedPct(data?.return_percentage)}</div>
        </div>
        {/* supporting metrics — four even columns; first flush left, last
            flush right so the row hugs the card padding */}
        <div className="grid grid-cols-4 gap-2 pt-3 border-t border-border">
          {secondary.map((stat, i) => (
            <div
              key={stat.label}
              className={`min-w-0 ${
                i === 0
                  ? 'text-left'
                  : i === secondary.length - 1
                    ? 'text-right'
                    : 'text-center'
              }`}
            >
              <div className="text-caption text-muted-foreground truncate">{stat.label}</div>
              <div className="text-data text-foreground">{stat.value}</div>
            </div>
          ))}
        </div>
      </div>
      {/* next-step question + actions, folded into the card */}
      <div className="px-4 pt-3 pb-3.5 border-t border-border">
        <p className="text-body text-foreground mb-2.5">{QUESTION_COPY}</p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="h-7 text-label"
            disabled={disabled}
            onClick={onRunOptimizer}
          >
            Run optimizer
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-label text-muted-foreground hover:text-foreground"
            disabled={disabled}
            onClick={onViewReport}
          >
            View full report
          </Button>
        </div>
      </div>
    </div>
  )
}
